import {
  WmButton,
  WmModal,
  WmModalFooter,
  WmModalHeader,
  WmSnackbar
} from "@watermarkinsights/ripple-react";
import { useIntl } from "react-intl";
import {
  SettingsReportingYear,
  notifications
} from "@/features/common/interfaces/Interface";
import translate from "@/i18n/translate";
import CreateReportingYearForm, {
  CreateReportingYearFormInstance
} from "./CreateReportingYearForm";
import { useRef } from "react";
import { WmSnackbarCustomEvent } from "@watermarkinsights/ripple";

export interface CreateReportingYearModalProps {
  openReportingYearModalState: boolean;
  closeReportingYearModal: () => void;
  reportingYears: SettingsReportingYear[] | null;
  fetchReportingYears: () => void;
  updateReportingYear: SettingsReportingYear | null;
}

export default function CreateReportingYearModal({
  openReportingYearModalState,
  closeReportingYearModal,
  reportingYears,
  fetchReportingYears,
  updateReportingYear
}: CreateReportingYearModalProps): JSX.Element {
  const intl = useIntl();
  function closeButtonTriggered() {
    closeReportingYearModal();
  }

  const reportingYearRef = useRef<CreateReportingYearFormInstance>(null);

  const addNewReportingYear = () => {
    if (reportingYearRef.current) {
      reportingYearRef.current.getCsrfToken();
    }
  };
  let notifications: notifications[] = [];

  function addSnackBar(reportingYearName: string, requestType: string) {
    const snackbar: any = document.getElementById("wm-snackbar");
    const randomId = `snack-${Math.floor(Math.random() * 100) + 1}`;
    const newNotification = {
      id: randomId,
      message:
        requestType === "update"
          ? updateReportingYearMessage(reportingYearName)
          : createReportingYearMessage(reportingYearName),
      link: ""
    };
    notifications = [newNotification, ...notifications];
    snackbar.notifications = JSON.stringify(notifications);
  }

  function dismissSnackBar(ev: WmSnackbarCustomEvent<Object>) {
    const snackbar: any = document.getElementById(ev.target.id);
    notifications = notifications.filter(
      (snack: notifications) => snack.id !== ev.detail.id
    );
    snackbar.notifications = JSON.stringify(notifications);
  }

  const updateReportingYearMessage = (name: string) => {
    return intl.formatMessage(
      {
        id: "app.settings.reportingYear.update"
      },
      { name: name }
    );
  };

  const createReportingYearMessage = (name: string) => {
    return intl.formatMessage(
      {
        id: "app.settings.reportingYear.create"
      },
      { name: name }
    );
  };

  const getAddOrUpdateDetails = () => {
    if (updateReportingYear) {
      return [
        useIntl().formatMessage({
          id: "app.settings.reportingYearModal.updateBtn"
        }),
        useIntl().formatMessage({
          id: "app.settings.editReportingYears"
        })
      ];
    } else {
      return [
        useIntl().formatMessage({
          id: "app.settings.reportingYearModal.primaryBtn"
        }),
        useIntl().formatMessage({
          id: "app.settings.addReportingYears"
        })
      ];
    }
  };

  const [primaryBtn, modalHeading] = getAddOrUpdateDetails();

  const secondaryBtn = useIntl().formatMessage({
    id: "app.settings.reportingYearModal.secondaryBtn"
  });

  const modalSubHeading = useIntl().formatMessage({
    id: "app.settings.reportingYearModal.subheading"
  });

  const sortedReportingYears: SettingsReportingYear[] | null = reportingYears;
  sortedReportingYears?.sort((current, next) => {
    return (
      new Date(next.startDate).getTime() - new Date(current.startDate).getTime()
    );
  });

  return (
    <>
      <div id="preview-modal">
        <WmModal
          open={openReportingYearModalState}
          id="create-reporting-year-modal"
          elementToFocus="titleinput"
          uid="create-reporting-year-modal"
          onWmModalCloseTriggered={closeReportingYearModal}
          onWmModalSecondaryTriggered={closeButtonTriggered}
          onWmModalPrimaryTriggered={addNewReportingYear}
        >
          <WmModalHeader
            heading={modalHeading}
            subheading={modalSubHeading}
          ></WmModalHeader>
          <div
            className="modal-content body"
            id="content-create-reporting-year-modal"
          >
            <div className="modal-content-heading">
              {translate("app.settings.reportingYearModal.requiredTextPart1")}
              <span className="modal-content-heading-asterisk"> * </span>
              {translate("app.settings.reportingYearModal.requiredTextPart2")}
            </div>
            <CreateReportingYearForm
              reportingYears={sortedReportingYears}
              openReportingYearModalState={openReportingYearModalState}
              ref={reportingYearRef}
              closeReportingYearModal={closeReportingYearModal}
              fetchReportingYears={fetchReportingYears}
              updateReportingYear={updateReportingYear}
              addSnackBar={addSnackBar}
            />
          </div>
          <WmModalFooter
            primaryText={primaryBtn}
            secondaryText={secondaryBtn}
            deleteStyle={false}
          ></WmModalFooter>
        </WmModal>
      </div>
      <WmSnackbar
        notifications="[]"
        id="wm-snackbar"
        onWmSnackbarSnackFinished={(event) => dismissSnackBar(event)}
      ></WmSnackbar>
    </>
  );
}
